import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { getAIModelIcon } from '@/lib/get-icon';
import { ScrollShadow } from '@nextui-org/react';

interface AIModelsListProps {
  className?: string;
  compact?: boolean;
  hideViewAll?: boolean;
}

export default function AIModelsList({
  className = '',
  compact = false,
  hideViewAll = false,
}: AIModelsListProps) {
  const router = useRouter();

  const handleModelClick = (model: string, e: React.MouseEvent) => {
    if (router.pathname === '/products') {
      e.preventDefault();
      router.push(
        {
          pathname: '/products',
          query: { ...router.query, aiModels: model },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  return (
    <div className={`relative ${className}`}>
      <ScrollShadow
        className="flex min-h-[36px] w-full gap-3 py-0.5 px-2"
        orientation="horizontal"
        hideScrollBar={true}
      >
        <div className="flex shrink-0 items-center gap-2.5 rounded-full bg-gray-50/70 px-3.5 py-1.5 backdrop-blur-sm dark:bg-gray-900/50">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Popular:
          </span>

          <div className="flex gap-2">
            <Link
              href="/products?aiModels=gpt"
              className="flex shrink-0 items-center rounded-full bg-white px-3 py-1 text-gray-600 shadow-[0_0_0_1px_rgba(0,0,0,0.05)] transition-all hover:bg-gray-50 hover:text-gray-900 hover:shadow-[0_0_0_1px_rgba(0,0,0,0.08)] dark:bg-gray-800 dark:text-gray-300 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.05)] dark:hover:bg-gray-700 dark:hover:text-gray-100"
              onClick={(e) => handleModelClick('gpt', e)}
            >
              {getAIModelIcon({
                iconName: 'gpt',
                className: 'mr-1.5 h-3.5 w-3.5',
              })}
              <span className="text-xs font-medium">ChatGPT</span>
            </Link>

            <Link
              href="/products?aiModels=midjourney"
              className="flex shrink-0 items-center rounded-full bg-white px-3 py-1 text-gray-600 shadow-[0_0_0_1px_rgba(0,0,0,0.05)] transition-all hover:bg-gray-50 hover:text-gray-900 hover:shadow-[0_0_0_1px_rgba(0,0,0,0.08)] dark:bg-gray-800 dark:text-gray-300 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.05)] dark:hover:bg-gray-700 dark:hover:text-gray-100"
              onClick={(e) => handleModelClick('midjourney', e)}
            >
              {getAIModelIcon({
                iconName: 'midjourney',
                className: 'mr-1.5 h-3.5 w-3.5',
              })}
              <span className="text-xs font-medium">Midjourney</span>
            </Link>

            <Link
              href="/products?aiModels=dall-e"
              className="flex shrink-0 items-center rounded-full bg-white px-3 py-1 text-gray-600 shadow-[0_0_0_1px_rgba(0,0,0,0.05)] transition-all hover:bg-gray-50 hover:text-gray-900 hover:shadow-[0_0_0_1px_rgba(0,0,0,0.08)] dark:bg-gray-800 dark:text-gray-300 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.05)] dark:hover:bg-gray-700 dark:hover:text-gray-100"
              onClick={(e) => handleModelClick('sora', e)}
            >
              {getAIModelIcon({
                iconName: 'sora',
                className: 'mr-1.5 h-3.5 w-3.5',
              })}
              <span className="text-xs font-medium">Sora</span>
            </Link>

            <Link
              href="/products?aiModels=flux-ai"
              className="flex shrink-0 items-center rounded-full bg-white px-3 py-1 text-gray-600 shadow-[0_0_0_1px_rgba(0,0,0,0.05)] transition-all hover:bg-gray-50 hover:text-gray-900 hover:shadow-[0_0_0_1px_rgba(0,0,0,0.08)] dark:bg-gray-800 dark:text-gray-300 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.05)] dark:hover:bg-gray-700 dark:hover:text-gray-100"
              onClick={(e) => handleModelClick('flux-ai', e)}
            >
              {getAIModelIcon({
                iconName: 'flux-ai',
                className: 'mr-1.5 h-3.5 w-3.5',
              })}
              <span className="text-xs font-medium">Flux AI</span>
            </Link>

            <Link
              href="/products?refineBy=generator"
              className="flex shrink-0 items-center rounded-full bg-white px-3 py-1 text-gray-600 shadow-[0_0_0_1px_rgba(0,0,0,0.05)] transition-all hover:bg-gray-50 hover:text-gray-900 hover:shadow-[0_0_0_1px_rgba(0,0,0,0.08)] dark:bg-gray-800 dark:text-gray-300 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.05)] dark:hover:bg-gray-700 dark:hover:text-gray-100"
              onClick={(e) => handleModelClick('flux-ai', e)}
            >
              {getAIModelIcon({
                iconName: 'flux-ai',
                className: 'mr-1.5 h-3.5 w-3.5',
              })}
              <span className="text-xs font-medium">LoRA</span>
            </Link>
          </div>
        </div>

        {!hideViewAll && (
          <Link
            href="/products"
            className="flex shrink-0 items-center rounded-full px-3 py-1 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <span className="text-xs font-medium">View all</span>
            <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
          </Link>
        )}
      </ScrollShadow>
    </div>
  );
}
